import {
	Document,
	Text,
	Page,
	StyleSheet,
	Image,
	PDFDownloadLink,
	View,
	Font,
	// PDFViewer,
} from '@react-pdf/renderer';

interface Props {
	data: Params;
}

interface Params {
	name: string;
	caseName: string;
	firmName: string;
	fullName: string;
	companyName: string;
	companyAlias: string;
	eligibilityCriteria: string;
	requiredDocuments: string;
	lawyerSign: string;
	userSign: string;
	signedAt: string;
}

Font.register({
	family: 'Praise',
	src: '/fonts/Praise-Regular.ttf',
});

Font.register({
	family: 'Poppins-Bold',
	src: '/fonts/Poppins-Bold.ttf',
});

Font.register({
	family: 'Poppins-SemiBold',
	src: '/fonts/Poppins-SemiBold.ttf',
});

Font.registerHyphenationCallback((word) =>
	word.length > 20 ? word.split('') : [word]
);

const styles = StyleSheet.create({
	page: {
		padding: 30,
	},
	logo: {
		width: '250px',
		height: 'auto',
	},
	text: {
		color: '#000',
		fontSize: 12,
		fontWeight: 400,
		lineHeight: 1.5,
	},
	space: {
		marginBottom: '15px',
	},
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		marginBottom: 10,
	},
	containerSignLawyer: {
		alignItems: 'flex-end',
	},
	containerSignUser: {},
});

export const RetainerDocumentPdf: React.FC<Props> = ({ data }) => {
	const {
		name,
		caseName,
		firmName,
		fullName,
		companyName,
		companyAlias,
		eligibilityCriteria,
		requiredDocuments,
		lawyerSign,
		userSign,
		signedAt,
	} = data;
	return (
		<Document>
			<Page size={'A4'} style={styles.page}>
				<View style={styles.container}>
					<Image src={'/logos/id-logo.png'} style={styles.logo} />
				</View>
				<View style={styles.container}>
					<Text style={{ fontSize: 24, fontFamily: 'Poppins-Bold' }}>
						Retainer Agreement
					</Text>
				</View>
				<View style={{ ...styles.container, marginBottom: 20 }}>
					<Text style={{ fontSize: 16, fontFamily: 'Poppins-SemiBold' }}>
						{caseName}
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>Dear {`${name}`}:</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						This agreement is made between the law firm {`${firmName}`}{' '}
						(&rdquo;Lowey&rdquo; or the &rdquo;Firm&rdquo;) and {`${fullName}`}{' '}
						(&rdquo;You&rdquo;) whereby You retain and employ the Firm to
						represent you in connection with potential claims against{' '}
						{`${companyName.trimEnd()}`}.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						The Firm will represent You as plaintiff on a contingency fee basis.
						This means: (1) that You will not be personally responsible for the
						payment of any legal fees or expenses to {`${companyAlias}`}{' '}
						regardless of the outcome of the case, and (2) that{' '}
						{`${companyAlias}`} will only receive compensation if they are
						successful at recovering money for You, for example as part of a
						settlement, at trial, or award following an arbitration.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						In the event that {`${companyAlias}`} is successful in recovering
						any money as part of a settlement, judgment, verdict, or otherwise,{' '}
						{`${companyAlias}`} shall be entitled to 33% of the total amount
						recovered and reimbursement of expenses. These fees and expenses may
						be paid from the settlement, judgment, or other award, or separately
						by one or more of the defendants in the action, and will not be
						charged to You. In the event a defendant agrees to pay fees and
						expenses separate from any recovery paid to You (e.g., as part of an
						agreement to pay attorney’s fees in an arbitration agreement){' '}
						{`${companyAlias}`} will be entitled to the maximum amount of
						attorney&apos;s fees and expenses available under that agreement.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						We anticipate that litigation expenses will include filing fees,
						court reporting and transcript costs, expert fees, research fees,
						document copying, reasonable travel costs, overnight delivery
						charges, and other office expenses related to the work performed on
						Your case. {`${companyAlias}`} will advance all litigation expenses
						on Your behalf and, if the case is a class action, on behalf of the
						class.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						In order to prosecute your claims, we may associate with one or more
						law firms, for example as local counsel in a specific jurisdiction,
						or as additional plaintiffs&apos; counsel to support the work being
						performed on the case. Any such firms that agree to be named as
						counsel for you will be bound by the same terms and conditions as
						set forth herein.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						We shall keep you advised of all material events in this litigation,
						as is our responsibility as attorneys. We shall provide you with
						copies of all significant pleadings, motions, or settlement demands
						in the action and may ask you to review and approve certain items
						before we can take action on Your behalf. We will have reasonable
						cooperation from You throughout the case, for example, to respond to
						discovery or other requests for information likely to be received
						from defendants, or offers to resolve your claims.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						As a plaintiff, you will have certain other responsibilities
						including, keeping current as to how the case is progressing,
						responding to our requests for documents or information, making
						yourself reasonably available for deposition, assisting with the
						collection and production of documents, and appearing at a trial or
						arbitration, if necessary.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						You should immediately preserve and stop the deletion or destruction
						of all documents, data, and devices (e.g., like your cell phone or
						computers) that may be related to the litigation, including, but not
						limited to, anything from the period from at least{' '}
						{`${eligibilityCriteria}`}.{' '}
						{`${
							requiredDocuments
								? `This includes the following documents: ${requiredDocuments}.`
								: ''
						}`}
						We are available to assist you with this process and to answer any
						questions.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						You shall have the right at any time to terminate our services and
						representation upon written notice. If this occurs, You still will
						not owe {`${companyAlias}`} for fees and expenses, however,{' '}
						{`${companyAlias}`} will be entitled to seek reimbursement for the
						fees and expenses incurred prior to termination from any eventual
						settlement, verdict, award, or other resolution reached in the case
						(e.g., should you retain other lawyers and they reach a settlement
						on behalf of you or a class of similarly situated individuals).
						Unless specifically agreed by you and {`${companyAlias}`},{' '}
						{`${companyAlias}`} will provide no further services and advance no
						further costs on your behalf after receipt of the notice. In certain
						circumstances, you may have the right to arbitration of fee disputes
						under 22 N.Y. C.R.R. 137.
					</Text>
				</View>

				<View style={styles.space}>
					<Text style={styles.text}>
						We also reserve the right to terminate our representation of You at
						any time. This includes if You fail to respond to our communications
						or participate in the action, or if there is a disagreement about
						how to proceed with the prosecution of Your case.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						We make no warranties or representations concerning the eventual
						outcome of any action in which you serve as a plaintiff and/or class
						representative.
					</Text>
				</View>
				<View style={styles.space}>
					<Text style={styles.text}>
						If you find everything in order, please sign below. We greatly
						appreciate the opportunity to be of service to you and look forward
						to working on your behalf in obtaining favorable results.
					</Text>
				</View>
				<View style={styles.containerSignLawyer}>
					<View style={{ width: '50%' }}>
						<Text style={styles.text}>Best,</Text>
						<Text style={{ fontFamily: 'Praise', fontSize: 36 }}>
							{lawyerSign}
						</Text>
					</View>
				</View>
				<View style={styles.containerSignUser}>
					<Text style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>
						ACCEPTED AND AGREED TO:
					</Text>
					<Text style={styles.text}>Signature</Text>
					<Text
						style={{
							fontFamily: 'Praise',
							fontSize: 36,
							marginLeft: 30,
							marginBottom: 12,
						}}
					>
						{userSign}
					</Text>
					<Text style={styles.text}>Date</Text>
					<Text style={{ ...styles.text, marginLeft: 30 }}>{signedAt}</Text>
				</View>
			</Page>
		</Document>
	);
};

export const PDFDownloadLinkComponent = ({ data }: Props) => {
	return (
		<PDFDownloadLink
			document={<RetainerDocumentPdf data={data} />}
			fileName={data.fullName}
		></PDFDownloadLink>
	);
};
